import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { addDays, addMonths, addWeeks } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useViewCalendarYear from 'src/hooks/useViewCalendarYear';
import useCreateCalendarYearApi from 'src/services/useCreateCalendarYearApi';
import useCreateNoNCalendarYearApi from 'src/services/useCreateNoNCalendarYearApi';
import CalendarYearModal from './CalendarYearModal';
import NoNCalendarYearModal from './NoNCalendarYearModal';
import useCreateNewYearErrors from './useCreateNewYearErrors';

export interface CalendarYear {
  yearCount: number;
  years: string;
  selected: boolean;
}

export interface DropdownPeriodType {
  id: 0 | 1 | 2;
  type: 'Week(s)' | 'Month(s)' | 'Day(s)';
}

export interface NoNCalendarYear {
  startDate: Date;
  noOfPeriods: number;
  periodLength: number;
  periodType: DropdownPeriodType;
  endDate: Date;
}

const generateFutureFYOption = (fiscalYear: number): CalendarYear[] => [
  { yearCount: 1, years: fiscalYear.toString(), selected: true },
  { yearCount: 2, years: `${fiscalYear}, ${fiscalYear + 1}`, selected: false },
  {
    yearCount: 3,
    years: `${fiscalYear}, ${fiscalYear + 1}, ${fiscalYear + 2}`,
    selected: false,
  },
];

export const calcInitialNonCalendarYear: (startDate: Date) => NoNCalendarYear = (
  startDate: Date
) => ({
  startDate: startDate,
  noOfPeriods: 12,
  periodLength: 1,
  periodType: startDate.getDate() < 29 ? { id: 2, type: 'Month(s)' } : { id: 1, type: 'Week(s)' },
  endDate: startDate.getDate() < 29 ? addMonths(startDate, 12) : addWeeks(startDate, 12),
});

const defaultNonCalendarYear = {
  noOfPeriods: 12,
  periodLength: 1,
  periodType: { id: 2, type: 'Month(s)' },
} as NoNCalendarYear;

const FiscalYearsTableGridToolbar = () => {
  const {
    t,
    latestFYPeriod,
    getGLPeriods,
    hasEditGLPeriodsPermission,
    isCalendarYear,
    isGlPeriodsLoading,
  } = useViewCalendarYear();
  const {
    company: { companyId },
  } = useCompany();

  const { createCalendarYear } = useCreateCalendarYearApi();
  const { createNoNCalendarYear } = useCreateNoNCalendarYearApi();
  const [calendarYear, setCalendarYear] = useState<CalendarYear[]>([]);
  const [nonCalendarYear, setNoNCalendarYear] = useState<NoNCalendarYear>(defaultNonCalendarYear);
  const {
    value: isOpenCalendarYearModal,
    setTrue: openCalendarYearModal,
    setFalse: closeCalendarYearModal,
  } = useBoolean(false);
  const {
    value: isOpenNoNCalendarYearModal,
    setTrue: openNoNCalendarYearModal,
    setFalse: closeNoNCalendarYearModal,
  } = useBoolean(false);

  const { isCalendarYearAboveLimit, totalYear } = useMemo(() => {
    const selectedFiscalYear = calendarYear.find((item) => item.selected) as CalendarYear;
    let isCalendarYearAboveLimit = false;
    const fiscalYears = selectedFiscalYear?.years?.split(',') ?? [];
    fiscalYears.forEach((year) => {
      if (Number(year) > 2099) {
        isCalendarYearAboveLimit = true;
        return;
      }
    });
    return {
      totalYear: selectedFiscalYear?.yearCount,
      isCalendarYearAboveLimit,
    };
  }, [calendarYear]);

  const handleCreateNew = useCallback(async () => {
    if (isOpenCalendarYearModal) {
      createCalendarYear({ companyId: Number(companyId), noOfYears: totalYear }).then(() => {
        toast.success('Fiscal Year(s) successfully created');
        getGLPeriods();
      });
      closeCalendarYearModal();
    }
  }, [
    closeCalendarYearModal,
    companyId,
    createCalendarYear,
    getGLPeriods,
    isOpenCalendarYearModal,
    totalYear,
  ]);

  const handleNoNCreateNewYear = useCallback(async () => {
    if (isOpenNoNCalendarYearModal) {
      createNoNCalendarYear({
        companyId: Number(companyId),
        noOfPeriods: nonCalendarYear?.noOfPeriods || 0,
        periodLength: nonCalendarYear.periodLength || 0,
        periodType: nonCalendarYear?.periodType?.id || 0,
      })
        .then(() => {
          toast.success('Fiscal Year successfully created');
          getGLPeriods();
        })
        .catch(() => {
          toast.error('Fiscal Year creation unsuccessfull');
        });
      closeNoNCalendarYearModal();
    }
  }, [
    closeNoNCalendarYearModal,
    companyId,
    createNoNCalendarYear,
    getGLPeriods,
    isOpenNoNCalendarYearModal,
    nonCalendarYear?.noOfPeriods,
    nonCalendarYear?.periodLength,
    nonCalendarYear?.periodType?.id,
  ]);

  const cancelNoNCalendarYearModal = useCallback(() => {
    closeNoNCalendarYearModal();
    if (latestFYPeriod?.yearEndDateObj) {
      setNoNCalendarYear(calcInitialNonCalendarYear(addDays(latestFYPeriod.yearEndDateObj, 1)));
    } else {
      setNoNCalendarYear(defaultNonCalendarYear);
    }
  }, [closeNoNCalendarYearModal, latestFYPeriod?.yearEndDateObj]);

  useEffect(() => {
    if (latestFYPeriod?.year) {
      if (isCalendarYear) {
        setCalendarYear(generateFutureFYOption(latestFYPeriod?.year + 1));
      } else {
        setNoNCalendarYear(calcInitialNonCalendarYear(addDays(latestFYPeriod.yearEndDateObj, 1)));
      }
    }
  }, [isCalendarYear, latestFYPeriod?.yearEndDateObj, latestFYPeriod?.year]);

  const isMonthValidationFailed = useMemo(
    () =>
      nonCalendarYear?.periodType?.type === 'Month(s)' &&
      nonCalendarYear?.startDate?.getDate() >= 29,
    [nonCalendarYear?.periodType?.type, nonCalendarYear?.startDate]
  );

  const {
    noOfPeriodsErrorList,
    PeriodsLengthErrorList,
    hasNoOfPeriodsErrors,
    hasPeriodsLengthErrors,
  } = useCreateNewYearErrors(
    nonCalendarYear?.noOfPeriods,
    nonCalendarYear?.periodLength,
    nonCalendarYear?.periodType
  );

  const isDateAboveLimit = useMemo(() => {
    const endDate = nonCalendarYear?.endDate;
    if (!endDate) return false;
    return endDate?.getFullYear() > 2099;
  }, [nonCalendarYear?.endDate]);

  const isNonCalendarYearCreateDisabled =
    isMonthValidationFailed ||
    hasNoOfPeriodsErrors ||
    hasPeriodsLengthErrors ||
    isDateAboveLimit ||
    !nonCalendarYear.startDate;

  return (
    <div className="flex items-center w-full">
      {hasEditGLPeriodsPermission && (
        <Button
          onClick={isCalendarYear ? openCalendarYearModal : openNoNCalendarYearModal}
          disabled={isGlPeriodsLoading}
        >
          Create New Year
        </Button>
      )}
      <Dialog
        id="modal"
        open={isOpenCalendarYearModal}
        onClose={closeCalendarYearModal}
        title={t('Create New Year(s)')}
        width="350px"
      >
        <CalendarYearModal calendarYear={calendarYear} setCalendarYear={setCalendarYear} />
        <DialogActionsBar>
          <div className="flex justify-end gap-2">
            <PopUp
              popupAlign={{ horizontal: 'left', vertical: 'bottom' }}
              anchorAlign={{ horizontal: 'right', vertical: 'center' }}
              className="max-h-[180px] "
              content={
                <div className="m-1 max-w-[350px]">
                  {isCalendarYearAboveLimit && (
                    <PopUpContentWrapper
                      type="Error"
                      contents={['Selected date exceeds the maximum limit of December 31, 2099.']}
                    />
                  )}
                </div>
              }
              show={isCalendarYearAboveLimit}
            >
              <Button color="info" onClick={handleCreateNew} disabled={isCalendarYearAboveLimit}>
                Create
              </Button>
            </PopUp>
            <Button color="info" onClick={closeCalendarYearModal}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
      <Dialog
        id="modal"
        open={isOpenNoNCalendarYearModal}
        onClose={cancelNoNCalendarYearModal}
        title={t('Create New Year(s)')}
        width="350px"
      >
        <NoNCalendarYearModal
          nonCalendarYear={nonCalendarYear}
          setNoNCalendarYear={setNoNCalendarYear}
          isMonthValidationFailed={isMonthValidationFailed}
          hasNoOfPeriodsErrors={hasNoOfPeriodsErrors}
          hasPeriodsLengthErrors={hasPeriodsLengthErrors}
        />
        <DialogActionsBar>
          <div className="flex justify-end gap-2">
            <PopUp
              popupAlign={{ horizontal: 'left', vertical: 'bottom' }}
              anchorAlign={{ horizontal: 'right', vertical: 'center' }}
              className="max-h-[180px] "
              content={
                <div className="m-1 max-w-[350px]">
                  {!nonCalendarYear.startDate && (
                    <PopUpContentWrapper type="Error" contents={['Start date must be entered']} />
                  )}
                  {isMonthValidationFailed && (
                    <PopUpContentWrapper
                      type="Error"
                      contents={["Month can't start on 29th-31st"]}
                    />
                  )}
                  {(hasNoOfPeriodsErrors || hasPeriodsLengthErrors) && (
                    <PopUpContentWrapper
                      type="Error"
                      contents={[...noOfPeriodsErrorList, ...PeriodsLengthErrorList]}
                    />
                  )}
                  {isDateAboveLimit && (
                    <PopUpContentWrapper
                      type="Error"
                      contents={['Selected date exceeds the maximum limit of December 31, 2099.']}
                    />
                  )}
                </div>
              }
              show={isNonCalendarYearCreateDisabled}
            >
              <Button
                color="info"
                onClick={handleNoNCreateNewYear}
                disabled={isNonCalendarYearCreateDisabled}
              >
                Create
              </Button>
            </PopUp>
            <Button color="info" onClick={cancelNoNCalendarYearModal}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </div>
  );
};

export default FiscalYearsTableGridToolbar;
